import Phaser from 'phaser';
import { COLORS, FONTS, GAME_HEIGHT, GAME_WIDTH, fontStr } from '@/config';
import { gameState } from '@/state/store';
import {
  BRAGE_KATEGORIER,
  KATEGORI_NAVN,
  type BrageKategori,
  type BrageNominasjon,
  type BrageVinner,
} from '@/systems/brage';
import { animateModalGlide } from '@/ui/modalGlide';
import { autoSpeed } from '@/systems/autoSpeed';

const SHEET_WIDTH = 1000;
const GULL = 0xb8860b;
const SKOGSGROENN = 0x3d5a4d;
const OKER = 0xd9a14b;

export type BrageModalType =
  | { type: 'nominasjon'; aar: number; nominasjoner: Record<BrageKategori, BrageNominasjon[]> }
  | { type: 'hint'; aar: number; toppKandidater: BrageNominasjon[] }
  | { type: 'vinner'; aar: number; vinnere: Record<BrageKategori, BrageVinner | null> };

export class BrageModal {
  private layer?: Phaser.GameObjects.Container;
  private innholdsContainer?: Phaser.GameObjects.Container;
  private mask?: Phaser.Display.Masks.GeometryMask;
  private scrollY = 0;
  private scrollMax = 0;
  private wheelHandler?: (_p: unknown, _g: unknown, _dx: number, dy: number) => void;
  private currentKey?: string;

  constructor(private scene: Phaser.Scene) {}

  open(data: BrageModalType): void {
    const key = this.byggKey(data);
    if (this.layer && this.currentKey === key) return;
    this.close();
    this.currentKey = key;
    this.layer = this.scene.add.container(0, 0).setDepth(200);
    animateModalGlide(this.scene, this.layer);

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.78)
      .setOrigin(0, 0)
      .setInteractive();
    this.layer.add(backdrop);

    const sheetX = (GAME_WIDTH - SHEET_WIDTH) / 2;
    const sheetY = 120;
    const sheetH = GAME_HEIGHT - 240;

    const sheet = this.scene.add
      .rectangle(sheetX, sheetY, SHEET_WIDTH, sheetH, COLORS.white, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(4, COLORS.wine, 1);
    this.layer.add(sheet);

    const venstreBorder = this.scene.add
      .rectangle(sheetX, sheetY, 8, sheetH, GULL, 1)
      .setOrigin(0, 0);
    this.layer.add(venstreBorder);

    const closeBtn = this.scene.add
      .text(sheetX + SHEET_WIDTH - 24, sheetY + 16, '×', {
        fontFamily: FONTS.display,
        fontSize: fontStr(56),
        color: '#1a1a1a',
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true });
    closeBtn.on('pointerup', () => this.close());
    this.layer.add(closeBtn);

    const maskShape = this.scene.make.graphics({});
    maskShape.fillStyle(0xffffff);
    maskShape.fillRect(sheetX + 16, sheetY + 80, SHEET_WIDTH - 32, sheetH - 200);
    this.mask = maskShape.createGeometryMask();

    const innhold = this.scene.add.container(sheetX + 32, sheetY + 80);
    innhold.setMask(this.mask);
    this.layer.add(innhold);
    this.innholdsContainer = innhold;

    this.wheelHandler = (_p, _g, _dx, dy) => this.scrollBy(dy);
    this.scene.input.on('wheel', this.wheelHandler);

    backdrop.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      const startY = pointer.y;
      const startScroll = this.scrollY;
      const onMove = (p: Phaser.Input.Pointer) => {
        const dy = p.y - startY;
        this.scrollY = Math.max(0, Math.min(this.scrollMax, startScroll - dy));
        if (this.innholdsContainer) this.innholdsContainer.y = sheetY + 80 - this.scrollY;
      };
      const onUp = () => {
        this.scene.input.off('pointermove', onMove);
        this.scene.input.off('pointerup', onUp);
      };
      this.scene.input.on('pointermove', onMove);
      this.scene.input.on('pointerup', onUp);
    });

    let y = 0;
    if (data.type === 'nominasjon') {
      y = this.renderNominasjon(innhold, data, y);
    } else if (data.type === 'hint') {
      y = this.renderHint(innhold, data, y);
    } else {
      y = this.renderVinner(innhold, data, y);
    }

    const synligHoyde = sheetH - 200;
    this.scrollMax = Math.max(0, y - synligHoyde);

    const knappH = 64;
    const knappW = 280;
    const knappX = sheetX + (SHEET_WIDTH - knappW) / 2;
    const knappY = sheetY + sheetH - knappH - 28;

    const knappBg = this.scene.add
      .rectangle(knappX, knappY, knappW, knappH, COLORS.wine, 1)
      .setOrigin(0, 0);
    this.layer.add(knappBg);

    const knappTekst = this.scene.add
      .text(knappX + knappW / 2, knappY + knappH / 2, this.knappTekst(data.type), {
        fontFamily: FONTS.hud,
        fontSize: fontStr(22),
        color: '#ffffff',
        fontStyle: '700',
      })
      .setOrigin(0.5);
    this.layer.add(knappTekst);

    knappBg.setInteractive({ useHandCursor: true });
    knappBg.on('pointerover', () => knappBg.setFillStyle(0x6f1f2c));
    knappBg.on('pointerout', () => knappBg.setFillStyle(COLORS.wine));
    knappBg.on('pointerup', () => this.close());

    if (data.type === 'vinner') {
      const spillerVinner = BRAGE_KATEGORIER.some(
        (k) => data.vinnere[k]?.forlag === 'spiller',
      );
      if (spillerVinner) this.kjorKonfetti();
    }
  }

  close(): void {
    if (this.wheelHandler) {
      this.scene.input.off('wheel', this.wheelHandler);
      this.wheelHandler = undefined;
    }
    this.layer?.destroy(true);
    this.layer = undefined;
    this.innholdsContainer = undefined;
    this.mask = undefined;
    this.scrollY = 0;
    this.scrollMax = 0;
    this.currentKey = undefined;
    autoSpeed.fortsett();
  }

  get isOpen(): boolean {
    return !!this.layer;
  }

  private scrollBy(dy: number): void {
    this.scrollY = Math.max(0, Math.min(this.scrollMax, this.scrollY + dy));
    if (this.innholdsContainer && this.layer) {
      const sheetY = 120;
      this.innholdsContainer.y = sheetY + 80 - this.scrollY;
    }
  }

  private byggKey(data: BrageModalType): string {
    if (data.type === 'nominasjon') return `nominasjon-${data.aar}`;
    if (data.type === 'hint') return `hint-${data.aar}`;
    return `vinner-${data.aar}`;
  }

  private knappTekst(type: BrageModalType['type']): string {
    if (type === 'nominasjon') return 'Lykke til  →';
    if (type === 'hint') return 'Spennende  →';
    return 'Fortsett  →';
  }

  private renderNominasjon(
    layer: Phaser.GameObjects.Container,
    data: { aar: number; nominasjoner: Record<BrageKategori, BrageNominasjon[]> },
    y: number,
  ): number {
    const innholdsBredde = SHEET_WIDTH - 64;

    const overskrift = this.scene.add
      .text(0, y, `Brage-prisen ${1900 + data.aar}`, {
        fontFamily: FONTS.display,
        fontSize: fontStr(44),
        color: '#862736',
      })
      .setOrigin(0, 0);
    layer.add(overskrift);
    y += 64;

    const undertekst = this.scene.add
      .text(0, y, 'Den norske Bokhandlerforening har offentliggjort nominasjonene', {
        fontFamily: FONTS.body,
        fontSize: fontStr(20),
        color: '#5a4a3a',
        fontStyle: 'italic',
        wordWrap: { width: innholdsBredde },
      })
      .setOrigin(0, 0);
    layer.add(undertekst);
    y += 48;

    for (const kategori of BRAGE_KATEGORIER) {
      y = this.renderKategoriListe(
        layer,
        innholdsBredde,
        y,
        kategori,
        data.nominasjoner[kategori],
      );
      y += 24;
    }
    return y;
  }

  private renderKategoriListe(
    layer: Phaser.GameObjects.Container,
    width: number,
    y: number,
    kategori: BrageKategori,
    nominasjoner: ReadonlyArray<BrageNominasjon>,
  ): number {
    const tittel = this.scene.add
      .text(0, y, KATEGORI_NAVN[kategori], {
        fontFamily: FONTS.display,
        fontSize: fontStr(28),
        color: '#1a1a1a',
      })
      .setOrigin(0, 0);
    layer.add(tittel);
    y += 44;

    if (nominasjoner.length === 0) {
      const tom = this.scene.add
        .text(0, y, 'Ingen nominasjoner i år', {
          fontFamily: FONTS.body,
          fontSize: fontStr(18),
          color: '#8b5e3c',
          fontStyle: 'italic',
        })
        .setOrigin(0, 0);
      layer.add(tom);
      return y + 28;
    }

    const radH = 84;
    for (let i = 0; i < nominasjoner.length; i += 1) {
      const nom = nominasjoner[i];
      const erSpiller = nom.forlag === 'spiller';
      const bg = this.scene.add
        .rectangle(
          0,
          y,
          width,
          radH,
          erSpiller ? COLORS.wine : 0xe9d8a8,
          erSpiller ? 0.12 : 0.5,
        )
        .setOrigin(0, 0);
      layer.add(bg);

      const venstre = this.scene.add
        .rectangle(0, y, 4, radH, erSpiller ? COLORS.wine : OKER, 1)
        .setOrigin(0, 0);
      layer.add(venstre);

      const tittelTxt = this.scene.add
        .text(16, y + 10, `${i + 1}. ${nom.tittel}`, {
          fontFamily: FONTS.display,
          fontSize: fontStr(20),
          color: erSpiller ? '#862736' : '#1a1a1a',
          wordWrap: { width: width - 220 },
        })
        .setOrigin(0, 0);
      layer.add(tittelTxt);

      const forlagTxt = erSpiller ? 'DITT FORLAG' : nom.forlag;
      const forfatterTxt = this.scene.add
        .text(16, y + radH - 12, `${nom.forfatterNavn} · ${forlagTxt}`, {
          fontFamily: FONTS.body,
          fontSize: fontStr(14),
          color: erSpiller ? '#862736' : '#5a4a3a',
          fontStyle: erSpiller ? '700' : 'italic',
        })
        .setOrigin(0, 1);
      layer.add(forfatterTxt);

      y += radH + 8;
    }
    return y;
  }

  private renderHint(
    layer: Phaser.GameObjects.Container,
    data: { aar: number; toppKandidater: BrageNominasjon[] },
    y: number,
  ): number {
    const innholdsBredde = SHEET_WIDTH - 64;
    const overskrift = this.scene.add
      .text(0, y, `Brage-jurien snart ferdig`, {
        fontFamily: FONTS.display,
        fontSize: fontStr(40),
        color: '#862736',
      })
      .setOrigin(0, 0);
    layer.add(overskrift);
    y += 64;

    let kropp = 'Ifølge kilder i juryen står striden';
    if (data.toppKandidater.length >= 2) {
      const a = data.toppKandidater[0];
      const b = data.toppKandidater[1];
      kropp += ` mellom «${a.tittel}» (${KATEGORI_NAVN[a.kategori]}) og «${b.tittel}» (${KATEGORI_NAVN[b.kategori]}) blant dine nominerte. Utdelingen er om fem dager.`;
    } else if (data.toppKandidater.length === 1) {
      const a = data.toppKandidater[0];
      kropp += ` om «${a.tittel}» (${KATEGORI_NAVN[a.kategori]}) — din eneste nominerte. Utdelingen er om fem dager.`;
    } else {
      kropp = 'Lekkasjer fra juryen tyder på spennende utdeling — men ingen av dine bøker er på kortlisten i år.';
    }

    const tekst = this.scene.add
      .text(0, y, kropp, {
        fontFamily: FONTS.body,
        fontSize: fontStr(22),
        color: '#1a1a1a',
        wordWrap: { width: innholdsBredde },
        lineSpacing: 8,
      })
      .setOrigin(0, 0);
    layer.add(tekst);
    return y + tekst.height + 24;
  }

  private renderVinner(
    layer: Phaser.GameObjects.Container,
    data: { aar: number; vinnere: Record<BrageKategori, BrageVinner | null> },
    y: number,
  ): number {
    const innholdsBredde = SHEET_WIDTH - 64;
    const overskrift = this.scene.add
      .text(0, y, `Brage-prisen ${1900 + data.aar} — vinnere`, {
        fontFamily: FONTS.display,
        fontSize: fontStr(40),
        color: '#862736',
      })
      .setOrigin(0, 0);
    layer.add(overskrift);
    y += 64;

    const undertekst = this.scene.add
      .text(0, y, 'Resultatene er klare. Kveldens vinnere er:', {
        fontFamily: FONTS.body,
        fontSize: fontStr(20),
        color: '#5a4a3a',
        fontStyle: 'italic',
      })
      .setOrigin(0, 0);
    layer.add(undertekst);
    y += 44;

    for (const kategori of BRAGE_KATEGORIER) {
      y = this.renderVinnerCelle(layer, innholdsBredde, y, kategori, data.vinnere[kategori]);
      y += 18;
    }
    return y;
  }

  private renderVinnerCelle(
    layer: Phaser.GameObjects.Container,
    width: number,
    y: number,
    kategori: BrageKategori,
    vinner: BrageVinner | null,
  ): number {
    const h = 140;
    const erSpiller = vinner?.forlag === 'spiller';

    const bg = this.scene.add
      .rectangle(0, y, width, h, COLORS.white, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.oakDark, 0.3);
    layer.add(bg);

    const venstre = this.scene.add
      .rectangle(0, y, 8, h, erSpiller ? GULL : OKER, 1)
      .setOrigin(0, 0);
    layer.add(venstre);

    const kat = this.scene.add
      .text(24, y + 14, KATEGORI_NAVN[kategori].toUpperCase(), {
        fontFamily: FONTS.hud,
        fontSize: fontStr(14),
        color: '#8b5e3c',
        fontStyle: '700',
      })
      .setOrigin(0, 0);
    layer.add(kat);

    if (!vinner) {
      const tom = this.scene.add
        .text(24, y + h / 2 + 8, 'Ingen vinner i år', {
          fontFamily: FONTS.body,
          fontSize: fontStr(18),
          color: '#8b5e3c',
          fontStyle: 'italic',
        })
        .setOrigin(0, 0.5);
      layer.add(tom);
      return y + h;
    }

    const tittel = this.scene.add
      .text(24, y + 50, vinner.tittel, {
        fontFamily: FONTS.display,
        fontSize: fontStr(24),
        color: '#1a1a1a',
        wordWrap: { width: width - 260 },
      })
      .setOrigin(0, 0);
    layer.add(tittel);

    const forfatterOgForlag = `${vinner.forfatterNavn} · ${erSpiller ? 'DITT FORLAG' : vinner.forlag}`;
    const forfatter = this.scene.add
      .text(24, y + h - 14, forfatterOgForlag, {
        fontFamily: FONTS.body,
        fontSize: fontStr(16),
        color: erSpiller ? '#862736' : '#5a4a3a',
        fontStyle: erSpiller ? '700' : 'italic',
      })
      .setOrigin(0, 1);
    layer.add(forfatter);

    if (erSpiller) {
      const padX = 12;
      const padY = 6;
      const badgeTxt = this.scene.add
        .text(0, 0, 'DITT FORLAG', {
          fontFamily: FONTS.hud,
          fontSize: fontStr(14),
          color: '#ffffff',
          fontStyle: '700',
        })
        .setOrigin(0, 0);
      const bw = badgeTxt.width + padX * 2;
      const bh = badgeTxt.height + padY * 2;
      const bx = width - bw - 18;
      const by = y + 16;
      const badgeBg = this.scene.add
        .rectangle(bx, by, bw, bh, SKOGSGROENN, 1)
        .setOrigin(0, 0);
      badgeTxt.setPosition(bx + padX, by + padY);
      layer.add(badgeBg);
      layer.add(badgeTxt);
    }

    return y + h;
  }

  private kjorKonfetti(): void {
    if (!this.layer) return;
    const partikler = 30;
    const farger = [COLORS.wine, GULL, OKER];
    const layer = this.scene.add.container(0, 0).setDepth(210);
    this.layer.add(layer);

    for (let i = 0; i < partikler; i += 1) {
      const x = Math.random() * GAME_WIDTH;
      const farge = farger[Math.floor(Math.random() * farger.length)];
      const stoerrelse = 6 + Math.floor(Math.random() * 8);
      const partikkel = this.scene.add
        .rectangle(x, -20, stoerrelse, stoerrelse, farge, 1)
        .setOrigin(0.5);
      partikkel.setAngle(Math.random() * 360);
      layer.add(partikkel);

      this.scene.tweens.add({
        targets: partikkel,
        y: GAME_HEIGHT + 40,
        angle: partikkel.angle + 360 * (Math.random() > 0.5 ? 1 : -1),
        alpha: 0,
        duration: 2500 + Math.random() * 1500,
        ease: 'Cubic.easeIn',
        delay: Math.random() * 600,
        onComplete: () => partikkel.destroy(),
      });
    }
  }
}

void gameState;
